import type {
  EvergreenMp4FailureCode,
  WebinarSessionConfiguration,
} from '@/features/webinar/types';
import { EVERGREEN_MP4_MAX_SIZE_BYTES } from '@/lib/evergreenMp4Upload';
import { inspectVideoFile, type VideoCodecInspection } from '@/lib/videoCodecValidation';

export type EvergreenMp4FileError =
  'NOT_MP4' | 'EMPTY' | 'TOO_LARGE' | 'UNSUPPORTED_CODEC' | 'DURATION_EXCEEDED';

export type EvergreenMp4UploadError =
  | EvergreenMp4FileError
  | EvergreenMp4FailureCode
  | 'UPLOAD_FAILED'
  | 'VALIDATION_TIMEOUT'
  | 'ABORTED'
  | 'SOURCE_TIMELINE_PREPARING'
  | 'SOURCES_SAVE_FAILED'
  | 'REPLACEMENT_REJECTED'
  | 'SOURCE_SESSION_DURATION_EXCEEDED'
  | 'SOURCE_SESSION_NOT_STARTED'
  | 'SOURCE_VIDEO_DURATION_UNAVAILABLE';

export const EVERGREEN_MP4_UPLOAD_ERRORS: readonly EvergreenMp4UploadError[] = [
  'NOT_MP4',
  'EMPTY',
  'TOO_LARGE',
  'UNSUPPORTED_CODEC',
  'DURATION_EXCEEDED',
  'SIZE_MISMATCH',
  'DOWNLOAD_FAILED',
  'PROBE_FAILED',
  'INTERNAL_ERROR',
  'SOURCE_SESSION_CHANGED',
  'INTERACTION_OUTSIDE_VIDEO_DURATION',
  'EVERGREEN_SESSION_IN_PROGRESS',
  'CUTS_DURATION_MISMATCH',
  'UPLOAD_FAILED',
  'VALIDATION_TIMEOUT',
  'ABORTED',
  'SOURCE_TIMELINE_PREPARING',
  'SOURCES_SAVE_FAILED',
  'REPLACEMENT_REJECTED',
  'SOURCE_SESSION_DURATION_EXCEEDED',
  'SOURCE_SESSION_NOT_STARTED',
  'SOURCE_VIDEO_DURATION_UNAVAILABLE',
];

const NON_RETRYABLE_UPLOAD_ERRORS: ReadonlySet<EvergreenMp4UploadError> = new Set([
  'NOT_MP4',
  'EMPTY',
  'TOO_LARGE',
  'UNSUPPORTED_CODEC',
  'DURATION_EXCEEDED',
  'INTERACTION_OUTSIDE_VIDEO_DURATION',
  'CUTS_DURATION_MISMATCH',
  'REPLACEMENT_REJECTED',
  'SOURCE_SESSION_DURATION_EXCEEDED',
  'SOURCE_SESSION_NOT_STARTED',
  'SOURCE_VIDEO_DURATION_UNAVAILABLE',
]);

/** Un Evergreen sans session Live source a été créé depuis une vidéo importée. */
export function isImportedMp4Evergreen(
  configuration: Pick<WebinarSessionConfiguration, 'type' | 'evergreenSession'> | null | undefined,
): boolean {
  return configuration?.type === 'EVERGREEN' && !configuration.evergreenSession;
}

/** Renvoyer le même fichier n'a de sens que si l'échec ne vient pas du fichier lui-même. */
export function isRetryableUploadError(error: EvergreenMp4UploadError): boolean {
  return !NON_RETRYABLE_UPLOAD_ERRORS.has(error);
}

export function checkEvergreenMp4File(
  file: Pick<File, 'name' | 'size'>,
): EvergreenMp4FileError | null {
  if (!file.name.toLowerCase().endsWith('.mp4')) return 'NOT_MP4';
  if (file.size <= 0) return 'EMPTY';
  if (file.size > EVERGREEN_MP4_MAX_SIZE_BYTES) return 'TOO_LARGE';
  return null;
}

type InspectDeps = {
  inspectCodec?: (file: File) => Promise<VideoCodecInspection>;
  readDurationSeconds?: (file: File) => Promise<number | null>;
};

export async function inspectEvergreenMp4Video(
  file: File,
  maxDurationSeconds: number | null,
  {
    inspectCodec = inspectVideoFile,
    readDurationSeconds = readVideoDurationSeconds,
  }: InspectDeps = {},
): Promise<EvergreenMp4FileError | null> {
  let inspection: VideoCodecInspection;
  try {
    inspection = await inspectCodec(file);
  } catch {
    inspection = { status: 'unknown' };
  }
  // Sur une vidéo longue non « faststart », la description du codec peut tomber hors des
  // octets sondés : on laisse passer l'inconnu, le validateur serveur tranchera.
  if (inspection.status === 'unsupported') return 'UNSUPPORTED_CODEC';

  if (maxDurationSeconds != null && maxDurationSeconds > 0) {
    const durationSeconds = await readDurationSeconds(file);
    if (durationSeconds != null && durationSeconds > maxDurationSeconds) return 'DURATION_EXCEEDED';
  }
  return null;
}

export function formatFileSize(bytes: number, locale: string): string {
  const french = locale.toLowerCase().startsWith('fr');
  const [value, unit] =
    bytes >= 1024 ** 3
      ? [bytes / 1024 ** 3, french ? 'Go' : 'GB']
      : [bytes / 1024 ** 2, french ? 'Mo' : 'MB'];
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)} ${unit}`;
}

const DURATION_READ_TIMEOUT_MS = 10_000;

export function readVideoDurationSeconds(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    let settled = false;
    const finish = (duration: number | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(duration);
    };
    const timeout = setTimeout(() => finish(null), DURATION_READ_TIMEOUT_MS);
    video.preload = 'metadata';
    video.onloadedmetadata = () =>
      finish(Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null);
    video.onerror = () => finish(null);
    video.src = url;
  });
}
